import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { invoke } from '~/lib/tauriInvoke'
import { errorText } from '~/lib/bots/addToGroupError'
import { camelToSnakeDeep } from '~/lib/platform/browser/caseConvert'
import { markAgentMcpToolAlwaysAllowed } from '~/composables/useAgentMcpServers'
import {
  AgentToolApprovalDecision,
  AgentToolApprovalStatus,
  type AgentToolApproval,
  type AgentToolApprovals,
} from '~/types/agent-credentials'

export { AgentToolApprovalDecision, AgentToolApprovalSource, AgentToolApprovalStatus } from '~/types/agent-credentials'
export type { AgentToolApproval } from '~/types/agent-credentials'

/** Server text for "someone (the chat button, another device) already decided this one". */
const ALREADY_DECIDED = /уже обработан/i

export type DecideResult =
  | { ok: true }
  /** Already decided elsewhere — the request is gone from the list, nothing to retry. */
  | { ok: false; stale: true; message: string }
  /** A real failure — the request is back in the list. */
  | { ok: false; stale: false; message: string }

function newestFirst(a: AgentToolApproval, b: AgentToolApproval) {
  return (b.created_at ?? '').localeCompare(a.created_at ?? '')
}

function str(value: unknown): string {
  return value == null ? '' : String(value)
}

/**
 * `AgentToolApprovalRequested` payload (camelCase `AgentToolApproval`, enums as numbers, times as
 * ISO strings) → the same snake_case shape `list_agent_tool_approvals` returns. Null if it names no id.
 */
export function parseRealtimeApproval(raw: Record<string, unknown>): AgentToolApproval | null {
  const a = camelToSnakeDeep<Record<string, unknown>>(raw)
  const id = str(a.id)
  if (!id) return null
  return {
    id,
    bot_id: str(a.bot_id),
    bot_name: str(a.bot_name),
    bot_avatar_url: str(a.bot_avatar_url),
    server_id: str(a.server_id),
    server_name: str(a.server_name),
    tool_name: str(a.tool_name),
    tool_title: str(a.tool_title),
    arguments_json: str(a.arguments_json),
    source: Number(a.source ?? 0),
    group_id: str(a.group_id),
    channel_id: str(a.channel_id),
    channel_name: str(a.channel_name),
    board_card_id: str(a.board_card_id),
    board_card_title: str(a.board_card_title),
    initiator_id: str(a.initiator_id),
    initiator_name: str(a.initiator_name),
    status: Number(a.status ?? AgentToolApprovalStatus.PENDING),
    created_at: str(a.created_at) || null,
    expires_at: str(a.expires_at) || null,
  }
}

/**
 * Agent tool calls waiting for the owner's decision («Ждут одобрения» on the agents page, plus the
 * badge on «Боты и агенты»). Loaded on every users-hub (re)connect and kept current by its
 * `AgentToolApprovalRequested` / `AgentToolApprovalResolved` events.
 */
export const useAgentApprovalsStore = defineStore('agentApprovals', {
  state: () => ({
    pending: [] as AgentToolApproval[],
    loaded: false,
    loading: false,
    /** Requests whose decision is in flight: a late `Requested` event must not bring them back. */
    deciding: {} as Record<string, AgentToolApprovalDecision>,
  }),
  getters: {
    count: (state) => state.pending.length,
  },
  actions: {
    async fetch() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await invoke<AgentToolApprovals>('list_agent_tool_approvals', { botId: null })
        this.pending = response.approvals
          .filter((a) => a.status === AgentToolApprovalStatus.PENDING && !this.deciding[a.id])
          .sort(newestFirst)
        this.loaded = true
      } catch (e) {
        // Not worth a toast: the list simply stays as it was until the next (re)connect.
        console.error('[AgentApprovals] Failed to load pending approvals', e)
        Sentry.captureException(e, { extra: { action: 'list_agent_tool_approvals' } })
      } finally {
        this.loading = false
      }
    },

    /** Optimistic: the request leaves the list at once and comes back if the backend refuses. */
    async decide(id: string, decision: AgentToolApprovalDecision): Promise<DecideResult> {
      if (this.deciding[id]) return { ok: true }
      const index = this.pending.findIndex((a) => a.id === id)
      const approval = index >= 0 ? this.pending[index] : undefined
      this.deciding[id] = decision
      if (index >= 0) this.pending.splice(index, 1)
      try {
        await invoke('decide_agent_tool_approval', { approvalId: id, decision })
        if (decision === AgentToolApprovalDecision.ALLOW_ALWAYS && approval) {
          markAgentMcpToolAlwaysAllowed(approval.bot_id, approval.server_id, approval.tool_name)
        }
        return { ok: true }
      } catch (e) {
        const message = errorText(e).replace(/^\[transient:[A-Z_]+\]\s*/, '').trim()
        if (ALREADY_DECIDED.test(message)) return { ok: false, stale: true, message }
        if (approval && !this.pending.some((a) => a.id === id)) {
          this.pending.push(approval)
          this.pending.sort(newestFirst)
        }
        console.error('[AgentApprovals] Failed to decide', id, e)
        return { ok: false, stale: false, message: message || 'Неизвестная ошибка. Попробуйте ещё раз.' }
      } finally {
        delete this.deciding[id]
      }
    },

    applyRealtime(type: 'AgentToolApprovalRequested' | 'AgentToolApprovalResolved', payload: Record<string, unknown>) {
      if (type === 'AgentToolApprovalRequested') {
        const approval = parseRealtimeApproval(payload)
        if (!approval || approval.status !== AgentToolApprovalStatus.PENDING || this.deciding[approval.id]) return
        const index = this.pending.findIndex((a) => a.id === approval.id)
        if (index >= 0) this.pending[index] = approval
        else this.pending.push(approval)
        this.pending.sort(newestFirst)
      } else {
        const id = str(payload.approvalId ?? payload.ApprovalId)
        if (!id) return
        this.pending = this.pending.filter((a) => a.id !== id)
      }
    },
  },
})
